import { createRoute, redirect } from "@tanstack/react-router"

import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { NotAuthenticatedError } from "@/lib/oauth/AuthController.ts"
import { MyOrgsPage } from "@/routes/auth/MyOrgsPage.tsx"
import { Navigator } from "@/routes/Navigator.ts"
import { RootRoute } from "@/routes/root/RootRoute.tsx"

import type { GlobalContext } from "@/lib/context.ts"

const log = getLogger("MyOrgsRoute")

export const MyOrgsRoute = createRoute({
  path: "/",
  component: () => <MyOrgsPage />,

  beforeLoad: async ({ context }) => {
    try {
      await withGlobalLoader(context, context.authController.awaitReady())
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        // Stored token is invalid - force logout and redirect to login
        await context.authController.logout()
        localStorage.setItem("redirect", location.toString())
        throw redirect(Navigator.login({ replace: true }))
      }

      log.error("awaitReady error", err)
      throw err
    }
  },

  loader: async ({ context }) => {
    const myOrgs = await withGlobalLoader(context, getMyOrgs(context))
    return { myOrgs }
  },

  getParentRoute: () => RootRoute
})

export interface OrgRef {
  id: string
  name: string
}

export interface OrgInvitationRef extends OrgRef {
  invitationToken: string
}

export interface MyOrgs {
  hasAccountIn: OrgRef[]
  hasInvitationTo: OrgInvitationRef[]
  canSignUpTo: OrgRef[]
}

const emptyOrgs: MyOrgs = {
  hasAccountIn: [],
  hasInvitationTo: [],
  canSignUpTo: [],
}

async function getMyOrgs(context: GlobalContext): Promise<MyOrgs> {
  try {
    const res = await fetch(new URL(`${context.host}/orgs/my`), {
      mode: "cors",
      headers: {
        authorization: (await context.authController.tokenSource.token()).accessToken,
      },
    })

    if (!res.ok) {
      return emptyOrgs
    }

    const orgsData = await res.json() as {
      email: string
      hasAccountIn: OrgRef[]
      hasInvitationTo: OrgInvitationRef[]
      canSignUpTo: OrgRef[]
    }

    function deserializeOrgRefArray(arr: OrgRef[]): OrgRef[] {
      return Array.isArray(arr)
        ? arr.map((item) => ({
          id: String(item.id),
          name: String(item.name),
        } as OrgRef))
        : []
    }

    function deserializeOrgInvitationRefArray(arr: OrgInvitationRef[]): OrgInvitationRef[] {
      return Array.isArray(arr)
        ? arr.map((item) => ({
          id: String(item.id),
          name: String(item.name),
          invitationToken: String(item.invitationToken),
        } as OrgInvitationRef))
        : []
    }

    return {
      hasAccountIn: deserializeOrgRefArray(orgsData.hasAccountIn),
      hasInvitationTo: deserializeOrgInvitationRefArray(orgsData.hasInvitationTo),
      canSignUpTo: deserializeOrgRefArray(orgsData.canSignUpTo),
    }
  } catch (_) {
    return emptyOrgs
  }
}
